import { useContext, useEffect, useMemo, useState } from 'react';
import { Flex, ListWithPagination, PaginationContext } from 'dash-ui';
import { useTranslation } from 'react-i18next';

import { PropertyNote } from 'gql/generated.graphql';
import { useGetPropertyNotesQuery } from 'gql/generated.graphql';

import PropertyNoteListItem from 'containers/properties/PropertyNote/components/PropertyNoteList/PropertyNoteListItem/PropertyNoteListItem';
import SetNoteForm from 'containers/properties/PropertyNote/components/SetNoteForm/SetNoteForm';

const PropertyNoteList = ({ propertyId }: { propertyId: string }) => {
  const { t } = useTranslation(['common', 'tables']);
  const {
    setTotal,
    state: paginationState,
    getRefetchProps,
  } = useContext(PaginationContext);
  const { skip, limit } = getRefetchProps(paginationState);
  const [editNodeId, setEditNodeId] = useState('');

  const {
    data: result,
    refetch,
    loading,
  } = useGetPropertyNotesQuery({
    variables: {
      propertyId,
      pagination: { skip, limit },
    },
  });

  const { data, total } = useMemo(() => {
    const notes = result?.getPropertyNotes?.collection || [];
    const filteredNotes = notes.filter((note) => {
      return note.authorAccount !== null || note.authorProfile !== null;
    });
    return {
      data: filteredNotes,
      total: filteredNotes.length,
    };
  }, [result]);

  useEffect(() => {
    setTotal(total);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [total]);

  useEffect(() => {
    refetch({
      propertyId,
      pagination: { skip, limit },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [skip, limit, propertyId]);

  return (
    <Flex w="100%">
      <ListWithPagination
        listProps={{
          loading,
          data,
          px: 0,
          pt: 0,
          placeholder: t('tables:propertyNote.placeholder') || '',
          flexDirection: 'column',
        }}
        paginationProps={{
          label: t('tables:propertyNote.paginationLabel') || '',
          previousButtonLabel: t('common:labels.previous'),
          nextButtonLabel: t('common:labels.next'),
        }}
      >
        {(note: PropertyNote) =>
          note._id === editNodeId ? (
            <SetNoteForm
              propertyNote={note}
              propertyId={propertyId}
              onCancel={() => setEditNodeId('')}
            />
          ) : (
            <PropertyNoteListItem item={note} onEditIconClick={setEditNodeId} />
          )
        }
      </ListWithPagination>
    </Flex>
  );
};

export default PropertyNoteList;
